import React, {useCallback, useEffect, useMemo, useState} from "react";
import {DialogActions, DialogContent, DialogTitle, Divider} from "@mui/material";
import {Row} from "reactstrap";
import Dialog from "../../base/dialog";
import useIsMounted from "../../../hooks/use-is-mounted";
import Api from "../../../../core/services/api/api";
import Button from "../../base/button/button";
import PartSupportRequestInformationRow from "../../app-specific/part-support/part-support-request-info/info-row";
import {PartSupportRequestInformationRowDataGridCellKeys} from "../../../../core/constants/table/data-grid-column-keys";
import AssignEntityCreditsToPartSupportTable from "../../app-specific/assign-entity-credits-to-part-support-table";
import ApiResponseCodes from "../../../../core/models/static/api-response-codes";
import DateUtils from "../../../../core/services/date-utils";

const initDuplicates = {credit: null, duplicates: []};

const AssignCreditToPartSupportRequestModal = ({partSupportRequest, open, setOpen, onSuccess}) => {
    const [duplicatedRGAsForCredit, setDuplicatedRGAsForCredit] = useState(initDuplicates);
    const isMounted = useIsMounted();

    const showDuplicateConfirmation = duplicatedRGAsForCredit?.duplicates?.length > 0;

    /**
     * Listens for the changes in keywords, and paginationInfo, and with each change:
     * - if the dialog is open, searches for the entries.
     */
    useEffect(() => {
        if (!open) {
            return setDuplicatedRGAsForCredit(initDuplicates);
        }
    }, [open])

    /**
     * Connects the given credit to the specified part support request.
     *
     * in case of any duplications, sets the duplicatedRGAsForCredit state to show any duplication and ask for user
     * confirmation.
     * @param credit
     * @param {boolean} duplicateConfirmation whether the user has agreed to duplicate RGAs being okay for this credit.
     */
    const connect = useCallback(async (credit, duplicateConfirmation = false) => {
        const forApi = {
            partSupportReqId: partSupportRequest.id,
            creditId: credit.id,
            duplicateConfirmation: duplicateConfirmation,
        }
        const response = await Api.assignCreditToPartSupportRequest(forApi);
        if (!isMounted()) return;
        if (response?.resultFlag) {
            setOpen(false);
            onSuccess(partSupportRequest, response.data);
            setOpen(false);
        } else {
            switch (response?.errorCode) {
                case ApiResponseCodes.duplicateCustomerCredit:
                    if (response?.data?.connectedRgaItemsToSelectedCredit?.length) {
                        return setDuplicatedRGAsForCredit({
                            credit: credit,
                            duplicates: response?.data?.connectedRgaItemsToSelectedCredit ?? [],
                        });
                    }
                    break;
                default:
                    break;
            }
        }
    }, [isMounted, onSuccess, partSupportRequest, setOpen])

    return (
        <>
            <Dialog
                open={open}
                setOpen={setOpen}
                maxWidth={showDuplicateConfirmation ? 'md' : 'md'}>
                <DialogTitle>
                    <Row xs={12} className='no-gutters justify-content-center w-100'>
                        <p className={`text-lg text-color-1 my-2 font-weight-semi-bold text-center`}>
                            Connect to a Credit
                        </p>
                    </Row>
                    <Divider className='divider'/>
                </DialogTitle>
                {
                    showDuplicateConfirmation
                        ? <ConnectedRgaItemsToSelectedCreditConfirmation
                            connect={connect}
                            duplicatedRGAsForCredit={duplicatedRGAsForCredit}
                            cancel={() => setDuplicatedRGAsForCredit(initDuplicates)}
                        />
                        : <CreditsView
                            open={open}
                            partSupportRequest={partSupportRequest}
                            connect={connect}
                        />
                }

            </Dialog>
        </>
    )
}

const CreditsView = ({partSupportRequest, connect}) => {
    const isMounted = useIsMounted();

    const dataGridSaveConfig = useMemo(() => ({
        name: 'AssignCreditToPartSupportRequestDialogCreditsViewDataGrid',
        version: 1,
    }), []);

    /**
     * Changes the selected keyword of the searching filter to trigger a searching api call.
     */
    const search = useCallback(async (keywords, paginationInfo) => {
        if (!partSupportRequest?.customer?.id)
            return;
        const forApi = {
            filters: {
                customerId: [partSupportRequest?.customer?.id],
                ToDate: DateUtils.serverNow().format(DateUtils.withoutOffsetFormat),
                Keyword: keywords,
            },
            paginationInfo,
        }
        const response = await Api.searchCredits(forApi);
        if (!isMounted())
            return;
        return {
            resultList: response?.data?.resultList?.map(e => ({
                number: e.invoiceNo,
                date: e.invoiceDate,
                poNumber: e.customerPO,
                total: e.total,
                id: e.id,
                originalData: e,
            })) ?? [],
            paginationInfo: {
                length: response?.data?.paginationInfo?.length ?? 0,
            }
        };
    }, [isMounted, partSupportRequest?.customer?.id])

    return (
        <>
            <DialogContent>
                <div className='w-100'>
                    <div className={'d-flex w-100'}>
                        <p className={'text-sm text-color-1'}>
                            Credit list for {partSupportRequest?.customer?.name ?? '--'}
                        </p>
                    </div>
                    <div className='my-3 w-100'/>
                    <AssignEntityCreditsToPartSupportTable
                        dataGridSaveConfig={dataGridSaveConfig}
                        search={search}
                        connect={connect}
                        includePagination
                    />
                </div>
                <div className='my-2 w-100'/>
            </DialogContent>
        </>
    )
}

const ConnectedRgaItemsToSelectedCreditConfirmation = ({
                                                           duplicatedRGAsForCredit,
                                                           connect: connectProps,
                                                           cancel,
                                                       }) => {
    const [connecting, setConnecting] = useState(false);
    const isMounted = useIsMounted();

    const DataGridSaveConfig = useMemo(() => ({
        name: 'AssignCreditToPartSupportRequestDialogDataGrid',
        version: 1,
    }), [])

    /**
     * Connects the given credit to the specified part support request.
     */
    const connect = async () => {
        setConnecting(true)
        await connectProps(duplicatedRGAsForCredit.credit, true);
        if (!isMounted()) return;
        setConnecting(false)
    }

    return (
        <>
            <DialogContent>
                <div className='w-100'>
                    <div className={'d-flex w-100'}>
                        <p className={'text-sm text-color-1 px-3'}>
                            The requested credit has been connected to the following RGAs before
                        </p>
                    </div>
                    <div className='my-3 w-100'/>
                    <PartSupportRequestInformationRow
                        data={duplicatedRGAsForCredit?.duplicates}
                        exclude={[
                            PartSupportRequestInformationRowDataGridCellKeys.vendorName,
                            PartSupportRequestInformationRowDataGridCellKeys.vendorCode,
                        ]}
                        saveConfig={DataGridSaveConfig}
                    />
                </div>
                <div className='my-2 w-100'/>
            </DialogContent>
            <DialogActions className={'p-0'}>
                <Row className={'w-100 no-gutters'}>
                    <Divider className='divider'/>
                    <Row className='no-gutters justify-content-between align-items-start w-100'>
                        <Button
                            disabled={connecting}
                            variant={'dialog'}
                            color={'primary'}
                            onClick={() => cancel()}
                            borderRadius={'0 8px 0 8px'}
                        >
                            Cancel
                        </Button>
                        <Button
                            disabled={connecting}
                            variant={'dialog'}
                            color={'primary'}
                            onClick={() => connect()}
                            borderRadius={'8px 0 8px 0'}
                        >
                            {
                                connecting
                                    ? "Connecting..."
                                    : "Connect"
                            }
                        </Button>
                    </Row>
                </Row>
            </DialogActions>
        </>
    )
}

export default AssignCreditToPartSupportRequestModal;
